import { faBicycle } from '@fortawesome/pro-regular-svg-icons/faBicycle';
import { faBus } from '@fortawesome/pro-regular-svg-icons/faBus';
import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { faWalking } from '@fortawesome/pro-regular-svg-icons/faWalking';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';
import ImportantPlacesBlank from '@propertypal/shared/src/resources/images/important-places/important-places-blank.svg?url';
import { ImportantPlaceResponse, TravelMode } from '@propertypal/shared/src/types/important-places';
import puralise from '@propertypal/shared/src/utils/puralise';
import router from 'next/router';
import React from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Bold, LinkText, Text } from '../typography';
import {
  ActionContainer,
  AddPlaceContainer,
  Container,
  ManagePlaceContainer,
  NoResultsContainer,
  NoResultsImg,
  Place,
  PlaceIcon,
  PlaceItem,
  PlaceList,
  PlaceTime,
  PlaceTravel,
  ResultsContainer,
} from './PropertyImportantPlaces.style.pn';

const travelMapper: Record<TravelMode, any> = {
  DRIVE: faCarSide,
  BICYCLE: faBicycle,
  WALK: faWalking,
  TRANSIT: faBus,
};

interface PropertyImportantPlacesProps {
  importantPlaces?: ImportantPlaceResponse[];
  showImportantPlacesModal: boolean;
  loggedIn: boolean;
  propertyPath: string;
}

const PropertyImportantPlaces: React.FC<PropertyImportantPlacesProps> = ({
  importantPlaces,
  showImportantPlacesModal,
  loggedIn,
  propertyPath,
}) => {
  const theme = useTheme();

  const onAddPlace = () => {
    if (!showImportantPlacesModal) {
      if (!loggedIn) router.push(`/login?sourcePath=${propertyPath}/important-places`);
      else router.replace(`${propertyPath}/important-places`, undefined, { shallow: true });
    }
  };

  return (
    <Container className="pn-important-places" data-testid="important-places">
      <Text fontSize={15} fontWeight="600" textTransform="uppercase" mb={16} color="#757575">
        Travel Time From This Property
      </Text>
      {importantPlaces?.length ? (
        <ResultsContainer data-testid="important-places-results">
          <PlaceList>
            {importantPlaces.map((p) => {
              const minutes = p.travelTime && Math.round(p.travelTime / 60);
              let travelTime: string = ``;
              if (p.travelTime == null || (minutes && minutes > 60)) travelTime = 'More than 60 minutes';
              else if (minutes && minutes <= 10) travelTime = `Less than 10 minutes`;
              else travelTime = `Approximately ${minutes} ${puralise('minute', 'minutes', minutes || 0)}`;

              return (
                <Place key={p.id} data-testid="important-place">
                  <PlaceIcon src="/map/important-place-marker.svg" />
                  <PlaceItem>{p.name}</PlaceItem>
                  <PlaceTravel>
                    <PlaceTime exceed={p.travelTime == null} data-testid="important-place-travel-time">
                      {travelTime}
                    </PlaceTime>
                    <FontAwesomeIcon icon={travelMapper[p.transport]} size={20} />
                  </PlaceTravel>
                </Place>
              );
            })}
          </PlaceList>
        </ResultsContainer>
      ) : (
        <NoResultsContainer data-testid="important-places-no-results">
          <NoResultsImg alt="No important places graphic" loading="lazy" src={ImportantPlacesBlank.src} />
          <Text fontSize={15} color="#757575">
            <Bold fontWeight="600" display="block">
              Important Places
            </Bold>
            <LinkText
              as="span"
              color={theme.textDark}
              textDecoration="none"
              onClick={onAddPlace}
              data-testid="add-place-1"
            >
              Add your own
            </LinkText>
            <span> important places to see how far they are from this property.</span>
          </Text>
        </NoResultsContainer>
      )}
      <ActionContainer>
        {(importantPlaces?.length || 0) < 5 && (
          <AddPlaceContainer onClick={onAddPlace} data-testid="add-place-2">
            <FontAwesomeIcon icon={faPlusCircle} size={20} />
            <span>Add a place</span>
          </AddPlaceContainer>
        )}
        {!!importantPlaces?.length && (
          <ManagePlaceContainer prefetch={false} href="/my/important-places" data-testid="manage-places">
            <FontAwesomeIcon icon={faMapMarkerAlt} size={20} />
            <span>Manage my places</span>
          </ManagePlaceContainer>
        )}
      </ActionContainer>
    </Container>
  );
};

export default PropertyImportantPlaces;
